"use client";

import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

import { AssetIcon } from "@/components/shared/asset-icon";

const MARKETS = [
  "META",
  "AMZN",
  "TSLA",
  "MSFT",
  "GOLD",
  "BTC",
  "ETH",
  "BNB",
  "XRP",
] as const;
const ROW_HEIGHT = 52;

export function RollingMarketTicker() {
  const reducedMotion = useReducedMotion();
  const [step, setStep] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  const items = useMemo(
    () => [
      ...MARKETS.slice(-2),
      ...MARKETS,
      ...MARKETS,
      ...MARKETS.slice(0, 2),
    ],
    [],
  );

  useEffect(() => {
    if (reducedMotion) return;

    if (step === MARKETS.length) {
      const reset = window.setTimeout(() => {
        setShouldAnimate(false);
        setStep(0);
      }, 650);
      return () => window.clearTimeout(reset);
    }

    const advance = window.setTimeout(() => {
      setShouldAnimate(true);
      setStep((current) => current + 1);
    }, 1650);
    return () => window.clearTimeout(advance);
  }, [reducedMotion, step]);

  const activeIndex = step + 2;

  return (
    <section
      className="flex w-full max-w-[23.75rem] flex-col items-center"
      aria-label="Trade stocks, gold, and crypto with stablecoins at the lowest fees"
    >
      <div className="grid grid-cols-[auto_138px] items-center justify-center gap-5 sm:grid-cols-[auto_150px] sm:gap-6">
        <span className="font-small text-[1.5625rem] font-medium tracking-[-0.04em] text-foreground sm:text-[1.6875rem]">
          Trade
        </span>

        <div
          className="relative h-[16.25rem] w-[8.625rem] overflow-hidden sm:w-[9.375rem]"
          aria-hidden="true"
          style={{
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, black 24%, black 76%, transparent 100%)",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, black 24%, black 76%, transparent 100%)",
          }}
        >
          <motion.div
            className="absolute inset-x-0 top-0"
            animate={{ y: -step * ROW_HEIGHT }}
            transition={
              shouldAnimate
                ? { duration: 0.62, ease: [0.32, 0.72, 0, 1] }
                : { duration: 0 }
            }
          >
            {items.map((symbol, index) => {
              const distance = Math.abs(index - activeIndex);
              return (
                <motion.div
                  key={`${symbol}-${index}`}
                  className="flex h-[3.25rem] items-center gap-2.5 whitespace-nowrap"
                  animate={{
                    opacity: distance === 0 ? 1 : distance === 1 ? 0.26 : 0.055,
                  }}
                  transition={{ duration: shouldAnimate ? 0.5 : 0 }}
                >
                  {symbol === "GOLD" ? (
                    <span
                      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-small text-xs font-semibold ${
                        distance === 0
                          ? "border-warning/35 bg-warning/15 text-warning dark:border-warning/35 dark:bg-warning/15 dark:text-warning"
                          : "border-warning/20 bg-warning/10 text-warning/80 dark:border-warning/20 dark:bg-warning/10 dark:text-warning/80"
                      }`}
                    >
                      Au
                    </span>
                  ) : (
                    <AssetIcon
                      symbol={symbol}
                      size={28}
                      className={
                        distance === 0
                          ? "ring-1 ring-black/15 dark:ring-white/15"
                          : "saturate-[0.7]"
                      }
                    />
                  )}
                  <span className="font-small text-[1.625rem] font-medium tracking-[-0.035em] text-foreground sm:text-[1.75rem]">
                    {symbol}
                  </span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>

      <p className="mt-8 text-center font-small text-[0.8125rem] font-normal tracking-[-0.01em] text-muted-foreground sm:text-sm">
        stocks, gold &amp; crypto — settled in stablecoins at the{" "}
        <span className="font-medium text-foreground/90">lowest fees.</span>
      </p>
    </section>
  );
}
